import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  SmartToy as BotIcon,
  Message as MessageIcon,
  PersonAdd as PersonAddIcon,
  Schedule as ScheduleIcon,
  FilterAlt as FilterIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';

const BOT_TYPES = [
  {
    id: 'message_ingestion',
    label: 'Message Ingestion Bot',
    description: 'Collects messages and files from channels for analysis',
    icon: <MessageIcon />,
    features: ['Real-time message collection', 'File download', 'Thread tracking', 'User mention tracking']
  },
  {
    id: 'entity_creation',
    label: 'Entity Creation Bot',
    description: 'Automatically creates entities from Slack user profiles and conversations',
    icon: <PersonAddIcon />,
    features: ['User profile analysis', 'Relationship mapping', 'Entity suggestions', 'Contact information extraction']
  }
];

const SYNC_SCHEDULES = [
  { value: '*/5 * * * *', label: 'Every 5 minutes' },
  { value: '*/15 * * * *', label: 'Every 15 minutes' },
  { value: '*/30 * * * *', label: 'Every 30 minutes' },
  { value: '0 * * * *', label: 'Every hour' },
  { value: '0 */6 * * *', label: 'Every 6 hours' },
  { value: '0 0 * * *', label: 'Daily at midnight' },
  { value: 'custom', label: 'Custom schedule' }
];

// eslint-disable-next-line max-lines-per-function
function SlackBotConfiguration({
  config = {},
  onConfigChange
}) {
  const [botConfig, setBotConfig] = useState({
    botType: 'message_ingestion',
    name: '',
    description: '',
    syncSchedule: '*/15 * * * *',
    customSchedule: '',
    timezone: 'America/New_York',
    realTimeSync: false,
    messageFilters: {
      includeThreads: true,
      includeFiles: true,
      includeBotMessages: false,
      includeEdited: true,
      includeDeleted: false,
      minMessageLength: 0,
      excludePatterns: []
    },
    entityCreation: {
      autoCreateUsers: false,
      requireApproval: true,
      confidenceThreshold: 0.7,
      extractContactInfo: true,
      mapRelationships: true
    },
    privacy: {
      tokenizeMessages: true,
      tokenizeUserInfo: true,
      retentionDays: 365
    },
    notifications: {
      onSyncComplete: false,
      onErrors: true,
      onNewEntities: true
    },
    ...config
  });

  const handleConfigUpdate = (newConfig) => {
    setBotConfig(newConfig);
    if (onConfigChange) {
      onConfigChange(newConfig);
    }
  };

  const updateField = (field, value) => {
    const newConfig = { ...botConfig, [field]: value };
    handleConfigUpdate(newConfig);
  };

  const updateNestedField = (section, field, value) => {
    const newConfig = {
      ...botConfig,
      [section]: {
        ...botConfig[section],
        [field]: value
      }
    };
    handleConfigUpdate(newConfig);
  };

  const selectedBotType = BOT_TYPES.find(type => type.id === botConfig.botType);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        <BotIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Bot Configuration
      </Typography>

      {/* Basic Configuration */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Basic Settings
          </Typography>

          <TextField
            fullWidth
            label="Bot Name"
            value={botConfig.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="e.g., Sales Team Message Bot"
            sx={{ mb: 2 }}
            required
          />

          <TextField
            fullWidth
            multiline
            rows={2}
            label="Description"
            value={botConfig.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="Describe what this bot will do..."
            sx={{ mb: 2 }}
          />
        </CardContent>
      </Card>

      {/* Bot Type Selection */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">
              <Typography variant="subtitle2">Bot Type</Typography>
            </FormLabel>
            <RadioGroup
              value={botConfig.botType}
              onChange={(e) => updateField('botType', e.target.value)}
              sx={{ mt: 1 }}
            >
              {BOT_TYPES.map((type) => (
                <Box key={type.id} sx={{ mb: 2 }}>
                  <FormControlLabel
                    value={type.id}
                    control={<Radio />}
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {type.icon}
                          <Typography variant="body1" fontWeight="medium">
                            {type.label}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {type.description}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {type.features.map((feature, index) => (
                            <Chip key={index} size="small" label={feature} variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                    }
                  />
                </Box>
              ))}
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* Sync Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon />
            <Typography variant="subtitle2">Sync Schedule</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={botConfig.realTimeSync}
                  onChange={(e) => updateField('realTimeSync', e.target.checked)}
                />
              }
              label="Real-time sync (webhooks)"
            />
            {botConfig.realTimeSync && (
              <Chip size="small" label="Premium Feature" color="primary" />
            )}
          </Box>

          {!botConfig.realTimeSync && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Sync Schedule</InputLabel>
                <Select
                  value={botConfig.syncSchedule === 'custom' ? 'custom' :
                         SYNC_SCHEDULES.find(s => s.value === botConfig.syncSchedule)?.value || 'custom'}
                  onChange={(e) => updateField('syncSchedule', e.target.value === 'custom' ? botConfig.syncSchedule : e.target.value)}
                  label="Sync Schedule"
                >
                  {SYNC_SCHEDULES.map(schedule => (
                    <MenuItem key={schedule.value} value={schedule.value}>
                      {schedule.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {(botConfig.syncSchedule === 'custom' ||
                !SYNC_SCHEDULES.find(s => s.value === botConfig.syncSchedule)) && (
                <TextField
                  fullWidth
                  label="Custom Cron Expression"
                  value={botConfig.customSchedule || botConfig.syncSchedule}
                  onChange={(e) => updateField('customSchedule', e.target.value)}
                  placeholder="*/15 * * * *"
                  helperText="Format: minute hour day month dayOfWeek"
                  sx={{ mb: 2 }}
                />
              )}

              <TextField
                fullWidth
                label="Timezone"
                value={botConfig.timezone}
                onChange={(e) => updateField('timezone', e.target.value)}
                sx={{ mb: 2 }}
              />
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Message Filters */}
      {selectedBotType?.id === 'message_ingestion' && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterIcon />
              <Typography variant="subtitle2">Message Filters</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              <ListItem>
                <ListItemText primary="Include thread messages" />
                <Switch
                  checked={botConfig.messageFilters.includeThreads}
                  onChange={(e) => updateNestedField('messageFilters', 'includeThreads', e.target.checked)}
                />
              </ListItem>
              <ListItem>
                <ListItemText primary="Include file attachments" />
                <Switch
                  checked={botConfig.messageFilters.includeFiles}
                  onChange={(e) => updateNestedField('messageFilters', 'includeFiles', e.target.checked)}
                />
              </ListItem>
              <ListItem>
                <ListItemText primary="Include bot messages" />
                <Switch
                  checked={botConfig.messageFilters.includeBotMessages}
                  onChange={(e) => updateNestedField('messageFilters', 'includeBotMessages', e.target.checked)}
                />
              </ListItem>
              <ListItem>
                <ListItemText primary="Include edited messages" />
                <Switch
                  checked={botConfig.messageFilters.includeEdited}
                  onChange={(e) => updateNestedField('messageFilters', 'includeEdited', e.target.checked)}
                />
              </ListItem>
              <ListItem>
                <ListItemText primary="Include deleted messages" />
                <Switch
                  checked={botConfig.messageFilters.includeDeleted}
                  onChange={(e) => updateNestedField('messageFilters', 'includeDeleted', e.target.checked)}
                />
              </ListItem>
            </List>

            <TextField
              fullWidth
              type="number"
              label="Minimum Message Length"
              value={botConfig.messageFilters.minMessageLength}
              onChange={(e) => updateNestedField('messageFilters', 'minMessageLength', parseInt(e.target.value) || 0)}
              helperText="Messages shorter than this will be ignored"
              sx={{ mt: 2 }}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Entity Creation Settings */}
      {selectedBotType?.id === 'entity_creation' && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonAddIcon />
              <Typography variant="subtitle2">Entity Creation</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Auto-create user entities"
                  secondary="Automatically create entities for new Slack users"
                />
                <Switch
                  checked={botConfig.entityCreation.autoCreateUsers}
                  onChange={(e) => updateNestedField('entityCreation', 'autoCreateUsers', e.target.checked)}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Require approval"
                  secondary="New entities require manual approval"
                />
                <Switch
                  checked={botConfig.entityCreation.requireApproval}
                  onChange={(e) => updateNestedField('entityCreation', 'requireApproval', e.target.checked)}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Extract contact information"
                  secondary="Extract email, phone, etc. from profiles"
                />
                <Switch
                  checked={botConfig.entityCreation.extractContactInfo}
                  onChange={(e) => updateNestedField('entityCreation', 'extractContactInfo', e.target.checked)}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Map relationships"
                  secondary="Detect relationships between users"
                />
                <Switch
                  checked={botConfig.entityCreation.mapRelationships}
                  onChange={(e) => updateNestedField('entityCreation', 'mapRelationships', e.target.checked)}
                />
              </ListItem>
            </List>

            <TextField
              fullWidth
              type="number"
              label="Confidence Threshold"
              value={botConfig.entityCreation.confidenceThreshold}
              onChange={(e) => updateNestedField('entityCreation', 'confidenceThreshold', parseFloat(e.target.value) || 0.7)}
              inputProps={{ min: 0, max: 1, step: 0.1 }}
              helperText="Minimum confidence required for entity creation (0.0 - 1.0)"
              sx={{ mt: 2 }}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Privacy Settings */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            <Typography variant="subtitle2">Privacy & Security</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 2 }}>
            FAANA automatically tokenizes sensitive information to protect privacy.
          </Alert>

          <List dense>
            <ListItem>
              <ListItemText
                primary="Tokenize message content"
                secondary="Replace sensitive data in messages with tokens"
              />
              <Switch
                checked={botConfig.privacy.tokenizeMessages}
                onChange={(e) => updateNestedField('privacy', 'tokenizeMessages', e.target.checked)}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Tokenize user information"
                secondary="Replace names, emails, etc. with tokens"
              />
              <Switch
                checked={botConfig.privacy.tokenizeUserInfo}
                onChange={(e) => updateNestedField('privacy', 'tokenizeUserInfo', e.target.checked)}
              />
            </ListItem>
          </List>

          <TextField
            fullWidth
            type="number"
            label="Data Retention (Days)"
            value={botConfig.privacy.retentionDays}
            onChange={(e) => updateNestedField('privacy', 'retentionDays', parseInt(e.target.value) || 365)}
            helperText="How long to keep ingested data (0 = forever)"
            sx={{ mt: 2 }}
          />
        </AccordionDetails>
      </Accordion>

      {/* Notifications */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon />
            <Typography variant="subtitle2">Notifications</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <List dense>
            <ListItem>
              <ListItemText primary="Notify on sync completion" />
              <Switch
                checked={botConfig.notifications.onSyncComplete}
                onChange={(e) => updateNestedField('notifications', 'onSyncComplete', e.target.checked)}
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="Notify on errors" />
              <Switch
                checked={botConfig.notifications.onErrors}
                onChange={(e) => updateNestedField('notifications', 'onErrors', e.target.checked)}
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="Notify on new entities" />
              <Switch
                checked={botConfig.notifications.onNewEntities}
                onChange={(e) => updateNestedField('notifications', 'onNewEntities', e.target.checked)}
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default SlackBotConfiguration;